// components/GuideParametresPointage.jsx
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Info, Settings, Clock, Coffee, Sun, Moon,
  ChevronDown, ChevronUp, Fingerprint, Camera,
  AlertCircle, CheckCircle, Timer, TrendingUp
} from 'lucide-react';

const COLOR = '#e8855a';
const COLOR_DARK = '#c96a3a';

const AccordionSection = ({ title, colorBar, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: colorBar }} />
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: colorBar + '20' }}>
            <Icon size={15} style={{ color: colorBar }} />
          </div>
          <span className="text-sm font-bold text-gray-800">{title}</span>
        </div>
        {isOpen ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 space-y-3 border-t border-gray-100 pt-4">
          {children}
        </div>
      )}
    </div>
  );
};

const GuideCard = ({ color = '#fdac84', label, desc, exemple, conseil }) => (
  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/30">
    <div className="flex items-start gap-3">
      <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: color }} />
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
        <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
        {exemple && (
          <p className="text-xs text-gray-400 mt-1.5">
            <span className="font-semibold text-gray-500">Ex :</span> {exemple}
          </p>
        )}
        {conseil && (
          <p className="text-xs mt-1.5 font-medium" style={{ color: COLOR_DARK }}>{conseil}</p>
        )}
      </div>
    </div>
  </div>
);

const StatutRow = ({ color, label, desc }) => (
  <div className="flex items-start gap-2.5">
    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: color }} />
    <p className="text-xs text-gray-500 leading-relaxed">
      <span className="font-semibold text-gray-700">{label} :</span> {desc}
    </p>
  </div>
);

const GuideParametresPointage = ({ onClose, rfidEnabled = false, photoEnabled = false }) => {
  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 bg-black/50" style={{ zIndex: 9998 }} />
      <div
        className="fixed right-0 w-full max-w-2xl bg-white shadow-2xl flex flex-col"
        style={{ zIndex: 9999, animation: 'hpSlideInRight 0.25s ease-out both', top: 0, bottom: 0, height: '100vh' }}
      >
        {/* HEADER */}
        <div className="flex-shrink-0 px-6 py-5 flex items-center justify-between" style={{ backgroundColor: COLOR }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
              <Settings size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white tracking-wide">Guide des paramètres de pointage</h2>
              <p className="text-xs text-white/75 mt-0.5">Configuration et règles de ponctualité</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white">
            <X size={18} />
          </button>
        </div>

        {/* INTRO */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-gray-100 bg-white">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
            <Info size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500 leading-relaxed">
              Ces paramètres définissent les règles de fonctionnement du système de pointage pour l'ensemble de votre organisation. Toute modification s'applique immédiatement à tous les employés. Configurez-les avec soin avant le démarrage.
            </p>
          </div>
        </div>

        {/* CONTENU */}
        <div className="flex-1 overflow-y-auto modal-scroll p-5 space-y-3" style={{ backgroundColor: '#fdfcf5' }}>

          {/* Mode pause */}
          <AccordionSection title="Mode de gestion des pauses" colorBar={COLOR} icon={Coffee} defaultOpen={true}>
            <div className="px-4 py-3 rounded-lg bg-orange-50 border border-orange-100 mb-1">
              <p className="text-xs text-orange-700 leading-relaxed">
                Ce paramètre détermine si les pauses déjeuner sont gérées automatiquement par le système ou si les employés doivent pointer leur sortie et retour de pause manuellement.
              </p>
            </div>
            <GuideCard
              color="#448696"
              label="Mode Automatique — 2 pointages par jour"
              desc="Le système déduit automatiquement la durée de pause configurée du temps de travail total. Les employés n'ont qu'à pointer leur arrivée et leur départ. La durée de pause est soustraite sans aucune action de leur part."
              exemple="Durée pause auto = 1h. Un employé qui arrive à 08h00 et part à 17h00 aura 8h de travail effectif (9h - 1h de pause)."
              conseil="Recommandé si tous vos employés ont la même pause et que vous ne souhaitez pas complexifier le pointage."
            />
            <GuideCard
              color={COLOR}
              label="Mode Pointage — 4 pointages par jour"
              desc="Les employés pointent 4 fois par jour : Arrivée, Sortie Pause, Retour Pause, Départ. Ce mode permet de suivre avec précision la durée réelle de chaque pause et de détecter les pauses excessives."
              exemple="Un employé sort en pause à 12h05 et revient à 13h20 — la durée réelle de 1h15 est enregistrée et comparée à la durée prévue."
              conseil="Recommandé si vous souhaitez un contrôle précis des pauses ou si les durées varient selon les employés."
            />
          </AccordionSection>

          {/* Mode pointage */}
          <AccordionSection title="Mode de pointage des employés" colorBar="#b08d1a" icon={Fingerprint} defaultOpen={true}>
            <div className="px-4 py-3 rounded-lg bg-amber-50 border border-amber-100 mb-1">
              <p className="text-xs text-amber-700 leading-relaxed">
                Ce paramètre définit la technologie utilisée pour identifier les employés au moment du pointage. Il conditionne l'interface affichée sur le kiosque de pointage.
              </p>
            </div>
            <GuideCard
              color="#b08d1a"
              label="Mode Manuel"
              desc="L'employé saisit son numéro de badge dans un champ de texte puis clique sur le bouton de validation. Le système vérifie le badge et enregistre le pointage. Une grille des pointages de la journée est affichée."
              conseil="Adapté si vous n'avez pas de lecteur RFID physique. Fonctionne sur tout ordinateur ou tablette."
            />
            {rfidEnabled && (
              <GuideCard
                color="#448696"
                label="Mode RFID"
                desc="L'employé approche sa carte RFID du lecteur physique connecté au poste de pointage. La détection est automatique — aucune saisie manuelle n'est requise. Le pointage s'effectue en moins d'une seconde."
                conseil="Nécessite un lecteur RFID USB connecté au poste de pointage. Activez ce mode uniquement si le matériel est en place."
              />
            )}
          </AccordionSection>

          {/* Configuration arrivée */}
          <AccordionSection title="Configuration de l'arrivée" colorBar="#22c55e" icon={Sun} defaultOpen={true}>
            <div className="px-4 py-3 rounded-lg bg-green-50 border border-green-100 mb-1">
              <p className="text-xs text-green-700 leading-relaxed">
                Deux niveaux de configuration : la plage horaire autorisée définit quand un pointage d'arrivée est accepté, et les règles de ponctualité définissent comment qualifier ce pointage.
              </p>
            </div>

            <GuideCard
              color="#22c55e"
              label="Plage horaire autorisée"
              desc="Définit la fenêtre de temps durant laquelle un pointage d'arrivée peut être enregistré. Tout pointage effectué en dehors de cette plage sera rejeté par le système avec un message d'erreur."
              exemple="Début 06h00 — Fin 10h00 : un employé qui tente de pointer à 10h30 recevra un message indiquant qu'il est hors période."
              conseil="Élargissez légèrement cette plage par rapport aux horaires réels pour ne pas bloquer les cas exceptionnels."
            />
            <GuideCard
              color="#b08d1a"
              label="Heure d'arrivée prévue"
              desc="L'heure officielle de début de journée de travail. C'est la référence utilisée pour calculer automatiquement le statut de ponctualité de chaque arrivée."
              exemple="Heure prévue = 07h30. Un employé arrivant à 07h25 sera marqué À temps."
            />
            <GuideCard
              color="#b08d1a"
              label="Tolérance (minutes)"
              desc="Nombre de minutes après l'heure prévue avant qu'un retard soit officiellement comptabilisé. Une tolérance de 20 minutes signifie qu'un employé arrivant jusqu'à 20 minutes après l'heure prévue est considéré En tolérance et non En retard."
              exemple="Heure prévue 07h30, tolérance 20 min. Arrivée à 07h45 = Tolérance. Arrivée à 07h51 = Retard."
              conseil="La tolérance est un intervalle de 10 minutes supplémentaires après lequel le statut passe de Tolérance à Retard."
            />

            {/* Statuts arrivée */}
            <div className="px-4 py-3 rounded-lg bg-gray-50 border border-gray-100">
              <p className="text-xs font-semibold text-gray-700 mb-2.5">Statuts d'arrivée calculés automatiquement</p>
              <div className="space-y-2">
                <StatutRow color="#448696" label="À temps" desc="Arrivée avant la limite de tolérance. Aucune anomalie." />
                <StatutRow color="#b08d1a" label="Tolérance" desc="Arrivée dans les 10 minutes suivant la limite de tolérance. Retard mineur non sanctionné." />
                <StatutRow color="#f97316" label="Retard" desc="Arrivée après la tolérance mais avant la fin de la plage horaire. Retard officiel enregistré." />
                <StatutRow color="#ef4444" label="Retard grave" desc="Arrivée après la fin de la plage horaire autorisée. Cas sérieux nécessitant une justification dans l'historique." />
              </div>
            </div>
          </AccordionSection>

          {/* Configuration départ */}
          <AccordionSection title="Configuration du départ" colorBar="#3182ce" icon={Moon} defaultOpen={false}>
            <div className="px-4 py-3 rounded-lg bg-blue-50 border border-blue-100 mb-1">
              <p className="text-xs text-blue-700 leading-relaxed">
                Même logique que pour l'arrivée : une plage horaire autorisée et des règles de ponctualité. Ici c'est le départ anticipé qui constitue l'anomalie principale à surveiller.
              </p>
            </div>
            <GuideCard
              color="#3182ce"
              label="Plage horaire autorisée"
              desc="Fenêtre de temps durant laquelle un pointage de départ est accepté. Un pointage de départ en dehors de cette plage sera rejeté."
              exemple="Début 15h00 — Fin 23h00 : un employé ne peut pointer son départ qu'à partir de 15h00."
            />
            <GuideCard
              color="#b08d1a"
              label="Heure de départ prévue"
              desc="L'heure officielle de fin de journée de travail. Référence pour calculer si un départ est normal, anticipé ou très anticipé."
              exemple="Heure prévue = 17h00. Un employé partant à 17h05 sera marqué À temps."
            />
            <GuideCard
              color="#b08d1a"
              label="Tolérance départ (minutes)"
              desc="Nombre de minutes avant l'heure prévue de départ pendant lesquelles un départ est considéré comme Départ anticipé acceptable et non comme Départ très anticipé."
              exemple="Heure prévue 17h00, tolérance 10 min. Départ à 16h55 = Départ anticipé. Départ à 16h40 = Départ très anticipé."
            />

            <div className="px-4 py-3 rounded-lg bg-gray-50 border border-gray-100">
              <p className="text-xs font-semibold text-gray-700 mb-2.5">Statuts de départ calculés automatiquement</p>
              <div className="space-y-2">
                <StatutRow color="#448696" label="À temps" desc="Départ à l'heure prévue ou après. Aucune anomalie." />
                <StatutRow color="#b08d1a" label="Départ anticipé" desc="Départ dans la plage de tolérance avant l'heure prévue. Acceptable sans justification." />
                <StatutRow color="#ef4444" label="Départ très anticipé" desc="Départ bien avant l'heure prévue, au-delà de la tolérance. Cas sérieux nécessitant une justification." />
              </div>
            </div>
          </AccordionSection>

          {/* Configuration pauses */}
          <AccordionSection title="Configuration des pauses" colorBar={COLOR} icon={Coffee} defaultOpen={false}>
            <div className="px-4 py-3 rounded-lg bg-orange-50 border border-orange-100 mb-1">
              <p className="text-xs text-orange-700 leading-relaxed">
                La configuration des pauses dépend du mode sélectionné. En mode Automatique, seule la durée de déduction est configurable. En mode Pointage, des plages horaires et des règles de ponctualité s'ajoutent.
              </p>
            </div>

            <GuideCard
              color="#448696"
              label="Mode Automatique — Durée de pause déduite"
              desc="Configurez ici le nombre d'heures et minutes qui seront automatiquement soustraites du temps de présence de chaque employé. Cette valeur s'applique uniformément à tous les employés sans exception."
              exemple="Durée = 01h00. Présence 08h00-17h00 = 9h de présence - 1h de pause = 8h de travail effectif."
              conseil="Assurez-vous que cette durée correspond bien à la pause légale ou conventionnelle applicable dans votre contexte."
            />
            <GuideCard
              color={COLOR}
              label="Mode Pointage — Plages horaires de pause"
              desc="Définissez les fenêtres de temps durant lesquelles les pointages de Sortie Pause et Retour Pause sont acceptés. Ces deux plages peuvent se chevaucher selon vos besoins."
              exemple="Sortie pause : 11h30-14h00 / Retour pause : 12h00-15h00. Un employé peut sortir entre 11h30 et 14h00 et revenir entre 12h00 et 15h00."
            />
            <GuideCard
              color={COLOR}
              label="Mode Pointage — Durée prévue et tolérance"
              desc="La durée prévue est la durée normale de la pause. La tolérance est le nombre de minutes supplémentaires acceptables avant que la pause soit qualifiée d'Excessive."
              exemple="Durée prévue = 60 min, tolérance = 10 min. Une pause de 68 min = normale. Une pause de 75 min = Pause excessive."
            />

            <div className="px-4 py-3 rounded-lg bg-gray-50 border border-gray-100">
              <p className="text-xs font-semibold text-gray-700 mb-2.5">Statuts de pause (mode Pointage uniquement)</p>
              <div className="space-y-2">
                <StatutRow color="#448696" label="Pause normale" desc="Durée de pause inférieure ou égale à la durée prévue + tolérance." />
                <StatutRow color="#ef4444" label="Pause excessive" desc="Durée de pause dépassant la durée prévue + tolérance. Cas à justifier dans l'historique." />
              </div>
            </div>
          </AccordionSection>

          {/* Photo anti-fraude */}
          {photoEnabled && (
            <AccordionSection title="Photo anti-fraude au pointage" colorBar="#718096" icon={Camera} defaultOpen={false}>
              <GuideCard
                color="#718096"
                label="Fonctionnement"
                desc="Lorsque cette option est activée, une fenêtre caméra s'ouvre automatiquement après chaque scan de badge. L'employé doit se prendre en photo avant que le pointage soit validé. La photo est enregistrée comme preuve de présence physique."
                conseil="Activez cette option si vous souhaitez empêcher qu'un employé pointe à la place d'un collègue absent."
              />
              <GuideCard
                color="#718096"
                label="Vérification automatique de la photo"
                desc="Le système vérifie automatiquement que la photo est valide : luminosité suffisante, résolution minimale, image non uniformément noire ou blanche. Une photo rejetée empêche la validation du pointage."
                exemple="Une photo prise dans l'obscurité totale ou avec un doigt devant l'objectif sera rejetée avec un message explicite."
                conseil="Assurez-vous que le poste de pointage est dans un endroit correctement éclairé pour éviter les rejets systématiques."
              />
              <GuideCard
                color="#ef4444"
                label="Impact sur le flux de pointage"
                desc="L'activation de la photo alourdit légèrement le processus de pointage — comptez 5 à 10 secondes supplémentaires par employé. Sur un kiosque partagé avec de nombreux employés, évaluez l'impact sur les temps d'attente aux heures de pointe."
                conseil="Combinez cette option avec le mode RFID pour limiter l'allongement du processus."
              />
            </AccordionSection>
          )}

          {/* Ordre de configuration recommandé */}
          <div className="px-5 py-4 rounded-xl border border-gray-200 bg-white">
            <p className="text-xs font-semibold text-gray-700 mb-2">Ordre de configuration recommandé</p>
            <div className="space-y-1.5">
              {[
                { num: '1', text: 'Choisissez le mode de pause (Automatique ou Pointage) selon vos besoins organisationnels.' },
                { num: '2', text: 'Choisissez le mode de pointage (Manuel ou RFID) selon le matériel disponible.' },
                { num: '3', text: 'Configurez les plages horaires d\'arrivée et de départ en laissant des marges raisonnables.' },
                { num: '4', text: 'Définissez les heures prévues d\'arrivée et de départ ainsi que les tolérances.' },
                { num: '5', text: 'Si mode Pointage : configurez les plages de pause et les règles de ponctualité associées.' },
                { num: '6', text: 'Cliquez sur Enregistrer — les paramètres s\'appliquent immédiatement à tous les pointages.' },
              ].map((item) => (
                <div key={item.num} className="flex items-start gap-2.5">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xs"
                    style={{ backgroundColor: COLOR }}
                  >
                    {item.num}
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed pt-0.5">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Avertissement */}
          <div className="px-4 py-3 rounded-xl border border-gray-200 bg-white">
            <p className="text-xs font-semibold text-gray-700 mb-1">Effets d'une modification en cours d'activité</p>
            <p className="text-xs text-gray-500 leading-relaxed">
              Toute modification des paramètres prend effet immédiatement. Les pointages déjà enregistrés ne sont pas recalculés — seuls les nouveaux pointages suivront les nouvelles règles. Évitez de modifier les tolérances ou les heures prévues en milieu de journée pour maintenir la cohérence des données.
            </p>
          </div>

        </div>

        {/* FOOTER */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between">
          <p className="text-xs text-gray-400">
            Les paramètres s'appliquent à <span className="font-semibold text-gray-600">toute l'organisation</span>
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg font-semibold text-xs border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
          >
            Fermer
          </button>
        </div>

      </div>
    </>,
    document.body
  );
};

export default GuideParametresPointage;